/**
 * Actr.ts - Actr - Universal Action Orchestration System
 * 
 * CHANGE HISTORY:
 * - 251102_1622 danielbeer: Integrate Profylr for profile-based permissions and context
 * - 251027_2147 danielbeer: Refactor to delegate CRUD operations to Aggregatr
 * - 251027_1810 danielbeer: Integrated Contextr for context management
 * - 251021_0517 [8786ca9] danielbeer: feat: apply authr pattern to all 43 remaining packages
 * - 251021_0447 [ce7b5e6] danielbeer: feat: automate TypeScript and Actr integration for all packages
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
// REMOVED: Direct imports cause circular dependencies with Dependr
// import { Aggregatr } from '@opcentral/aggregatr';
// import { ContextrManager } from '@opcentral/contextr';
// These are now injected via options or left as 'any' type
import type { ProfileManager, ResolvedProfile } from '@opcentral/profylr';
import type {
  ActrOptions,
  ActionConfig,
  ActionContext,
  MiddlewareCollection,
  BeforeMiddleware,
  AfterMiddleware,
  ErrorMiddleware,
  QueryFilters,
  EntityData
} from './types/ActrTypes';

export class Actr {
  options: ActrOptions & { debug: boolean; enableNotifications: boolean; enableUndoRedo: boolean };
  actions: Map<string, ActionConfig>;
  middleware: MiddlewareCollection;
  contextCache: ActionContext | null;
  contextCacheTime: number;
  contextCacheDuration: number;
  aggregatr: any; // Data layer - handles all CRUD operations (injected to avoid circular deps)
  profylr: ProfileManager | null; // Profile manager - provides context for all operations

  constructor(options: ActrOptions = {}) {
    this.options = {
      logger: options.logger || null,          // Loggr instance for logging
      debug: options.debug || false,
      contextProvider: options.contextProvider || null,
      contextr: options.contextr || undefined,      // Contextr instance for context management
      storage: options.storage || null,
      permittr: options.permittr || null,      // Permittr instance for permission checks
      notifyr: options.notifyr || null,        // Notifyr instance for user notifications
      backtrackr: options.backtrackr || null,  // Backtrackr instance for undo/redo
      profylr: options.profylr || null,        // Profylr instance for profile management
      enableNotifications: options.enableNotifications !== false, // Default: true
      enableUndoRedo: options.enableUndoRedo !== false,          // Default: true
      ...options
    };
    
    // Store Profylr reference
    this.profylr = this.options.profylr || null;

    // Contextr is optional - must be injected to avoid circular dependencies
    // If not provided, context features will be disabled
    if (!this.options.contextr) {
      console.warn('[Actr] Contextr not provided - context tracking disabled');
    }

    // Action registry
    this.actions = new Map();
    
    // Middleware
    this.middleware = {
      before: [],
      after: [],
      error: []
    };
    
    // Context cache
    this.contextCache = null;
    this.contextCacheTime = 0;
    this.contextCacheDuration = 1000; // 1 second

    // Aggregatr is optional - must be injected to avoid circular dependencies
    // If not provided, data operations will be disabled
    this.aggregatr = options.aggregatr || null;
    if (!this.aggregatr) {
      console.warn('[Actr] Aggregatr not provided - data operations disabled');
    }
    
    // Initialize automatic middleware
    this._initializeAutomaticMiddleware();
  }

  /**
   * Initialize automatic middleware for Notifyr and Backtrackr
   */
  private _initializeAutomaticMiddleware(): void {
    // Notifyr middleware - automatic notifications
    if (this.options.notifyr && this.options.enableNotifications) {
      // Success notifications (after middleware)
      this.use('after', async (action: string, _result: any, _context: ActionContext) => {
        const notifyr = this.options.notifyr;
        
        // CRUD operation notifications
        if (action.startsWith('create:')) {
          const entityType = action.split(':')[1];
          notifyr.success(`${this._capitalize(entityType)} created successfully`);
        } else if (action.startsWith('update:')) {
          // Silent update (too noisy)
        } else if (action.startsWith('delete:')) {
          const entityType = action.split(':')[1];
          notifyr.success(`${this._capitalize(entityType)} deleted`);
        }
      });
      
      // Error notifications (error middleware)
      this.use('error', async (action: string, error: Error, _context: ActionContext) => {
        const notifyr = this.options.notifyr;
        
        // Check if it's a permission error
        if (error.message && error.message.includes('Permission denied')) {
          notifyr.error('You don\'t have permission to perform this action');
        } else if (action.startsWith('create:')) {
          notifyr.error('Failed to create item');
        } else if (action.startsWith('update:')) {
          notifyr.error('Failed to update item');
        } else if (action.startsWith('delete:')) {
          notifyr.error('Failed to delete item');
        } else {
          notifyr.error('Operation failed');
        }
      });
      
      if (this.options.debug) {
        console.log('[ACTR] Notifyr middleware initialized');
      }
    }
    
    // Backtrackr middleware - automatic undo/redo recording
    if (this.options.backtrackr && this.options.enableUndoRedo) {
      // Record changes after successful operations
      this.use('after', async (action: string, result: any, _context: ActionContext) => {
        const backtrackr = this.options.backtrackr;
        
        // Only record CRUD operations
        if (action.startsWith('create:')) {
          const entityType = action.split(':')[1];
          backtrackr.recordChange({
            type: 'create',
            entityType,
            itemId: result.id,
            data: result
          });
        } else if (action.startsWith('update:')) {
          const entityType = action.split(':')[1];
          // Note: 'before' state should be passed in context by the update operation
          if ((result as any)._beforeState) {
            backtrackr.recordChange({
              type: 'update',
              entityType,
              itemId: result.id,
              before: (result as any)._beforeState,
              after: result
            });
          }
        } else if (action.startsWith('delete:')) {
          const entityType = action.split(':')[1];
          // Note: deleted entity should be passed in result
          if (result && (result as any)._deletedEntity) {
            backtrackr.recordChange({
              type: 'delete',
              entityType,
              itemId: (result as any)._deletedEntity.id,
              data: (result as any)._deletedEntity
            });
          }
        }
      });
      
      // Record errors
      this.use('error', async (action: string, result: any, _context: ActionContext) => {
        const backtrackr = this.options.backtrackr;
        
        // Only record CRUD operations
        if (action.startsWith('create:')) {
          const entityType = action.split(':')[1];
          backtrackr.recordError({
            type: 'create',
            entityType,
            itemId: result.id,
            data: result
          });
        } else if (action.startsWith('update:')) {
          const entityType = action.split(':')[1];
          // Note: 'before' state should be passed in context by the update operation
          if ((result as any)._beforeState) {
            backtrackr.recordError({
              type: 'update',
              entityType,
              itemId: result.id,
              before: (result as any)._beforeState,
              after: result
            });
          }
        } else if (action.startsWith('delete:')) {
          const entityType = action.split(':')[1];
          // Note: deleted entity should be passed in result
          if (result && (result as any)._deletedEntity) {
            backtrackr.recordError({
              type: 'delete',
              entityType,
              itemId: (result as any)._deletedEntity.id,
              data: (result as any)._deletedEntity
            });
          }
        }
      });
      
      if (this.options.debug) {
        console.log('[ACTR] Backtrackr middleware initialized');
      }
    }
  }

  /**
   * Capitalize first letter of string
   */
  private _capitalize(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }


  /**
   * Register an action handler
   */
  register(actionName: string, config: ActionConfig): void {
    if (!config.handler || typeof config.handler !== 'function') {
      throw new Error(`Action ${actionName} must have a handler function`);
    }

    this.actions.set(actionName, {
      handler: config.handler,
      contextRules: config.contextRules || [],
      permissions: config.permissions || [],
      logLevel: config.logLevel || 'info',
      description: config.description || '',
      metadata: config.metadata || {}
    });

    if (this.options.debug) {
      console.log(`[ACTR] Registered action: ${actionName}`);
    }
  }

  /**
   * Register an action handler (alias for backward compatibility)
   */
  registerAction(actionName: string, handler: Function, config?: Partial<ActionConfig>): void {
    this.register(actionName, {
      handler: handler as any,
      ...config
    });
  }

  /**
   * Unregister an action handler
   */
  unregister(actionName: string): boolean {
    const deleted = this.actions.delete(actionName);
    
    if (this.options.debug && deleted) {
      console.log(`[ACTR] Unregistered action: ${actionName}`);
    }
    
    return deleted;
  }

  /**
   * Add middleware
   */
  use(type: 'before' | 'after' | 'error', fn: BeforeMiddleware | AfterMiddleware | ErrorMiddleware): void {
    if (!['before', 'after', 'error'].includes(type)) {
      throw new Error(`Invalid middleware type: ${type}`);
    }

    if (typeof fn !== 'function') {
      throw new Error('Middleware must be a function');
    }

    this.middleware[type].push(fn);

    if (this.options.debug) {
      console.log(`[ACTR] Added ${type} middleware`);
    }
  }

  /**
   * Execute an action
   */
  async execute(actionName: string, params: any = {}): Promise<any> {
    const startTime = Date.now();

    try {
      // 1. Get action config
      const action = this.actions.get(actionName);
      if (!action) {
        throw new Error(`Action not registered: ${actionName}`);
      }

      // 2. Evaluate context
      const context = await this.getContext();

      // 3. Check context rules
      this.checkContextRules(action.contextRules, context);

      // 4. Check permissions (using Profylr profile + Permittr if available)
      await this.checkPermissions(
        action.permissions, 
        context, 
        actionName, 
        action.metadata?.resource || 'action',
        action.metadata?.resourceId || params.id || '*'
      );

      // 5. Run before middleware
      let processedParams = params;
      for (const middleware of this.middleware.before) {
        processedParams = await middleware(actionName, processedParams, context) || processedParams;
      }

      // 6. Log action start
      this.log('info', `[ACTR] Executing: ${actionName}`, {
        params: processedParams,
        context
      });

      // 7. Execute handler
      const result = await action.handler(processedParams, context);

      // 8. Run after middleware
      for (const middleware of this.middleware.after) {
        await middleware(actionName, result, context);
      }

      // 9. Log action completion
      const duration = Date.now() - startTime;
      this.log(action.logLevel || 'info', `[ACTR] Completed: ${actionName} (${duration}ms)`, {
        result,
        duration
      });

      return result;

    } catch (error) {
      // Run error middleware
      const context = await this.getContext();
      const err = error instanceof Error ? error : new Error(String(error));
      
      for (const middleware of this.middleware.error) {
        await middleware(actionName, err, context);
      }

      // Log error
      const duration = Date.now() - startTime;
      this.log('error', `[ACTR] Failed: ${actionName} (${duration}ms)`, {
        error: err,
        params
      });

      throw err;
    }
  }

  /**
   * Get current context
   * @returns {Promise<Object>} Current context
   */
  async getContext() {
    // Check cache
    const now = Date.now();
    if (this.contextCache && (now - this.contextCacheTime) < this.contextCacheDuration) {
      return this.contextCache;
    }

    // Evaluate context
    const context = await this.evaluateContext();
    
    // Cache it
    this.contextCache = context;
    this.contextCacheTime = now;
    
    return context;
  }

  /**
   * Evaluate current context
   * @returns {Promise<Object>} Context object
   */
  async evaluateContext() {
    // Use custom context provider if available
    if (this.options.contextProvider) {
      return await this.options.contextProvider();
    }

    // Use Contextr for context management
    const contextr = this.options.contextr;
    if (!contextr) {
      return {
        app: 'OpCentral',
        view: 'unknown',
        user: {},
        permissions: [],
        data: {},
        device: {},
        environment: {},
        network: {},
        system: {},
        timestamp: Date.now()
      };
    }
    const fullContext = await contextr.getCurrentContext();

    // Return Actr-compatible format
    return {
      app: fullContext.application.name,
      view: fullContext.application.route || 'unknown',
      user: fullContext.user,
      permissions: fullContext.user.permissions,
      data: fullContext.data,
      device: fullContext.device,
      environment: fullContext.environment,
      network: fullContext.network,
      system: fullContext.system,
      timestamp: fullContext.timestamp.getTime()
    };
  }

  /**
   * Get current app (delegates to Contextr)
   * @returns {string} App name
   */
  getCurrentApp(): string {
    // Kept for backward compatibility - delegates to Contextr
    return 'OpCentral';
  }

  /**
   * Get current view (delegates to Contextr)
   * @returns {string} View name
   */
  getCurrentView(): string {
    // Kept for backward compatibility - delegates to Contextr
    if (typeof window !== 'undefined') {
      return window.location.pathname;
    }
    return 'unknown';
  }

  /**
   * Get current user (delegates to Contextr)
   * @returns {Object} User object
   */
  async getCurrentUser() {
    const contextr = this.options.contextr;
    const userContext = await contextr.getUserContext();
    return userContext;
  }

  /**
   * Get user permissions (delegates to Contextr)
   * @returns {Array<string>} Permission list
   */
  async getUserPermissions() {
    const contextr = this.options.contextr;
    const userContext = await contextr.getUserContext();
    return userContext.permissions;
  }

  /**
   * Get data context (delegates to Contextr)
   * @returns {Object} Data context
   */
  async getDataContext() {
    const contextr = this.options.contextr;
    const dataContext = await contextr.getDataContext();
    return dataContext;
  }

  /**
   * Get active profile from Profylr
   * @returns {Promise<ResolvedProfile | null>} Active profile with inheritance resolved
   */
  async getActiveProfile(): Promise<ResolvedProfile | null> {
    if (!this.profylr) {
      return null;
    }
    
    try {
      return await this.profylr.getActiveProfile();
    } catch (error) {
      if (this.options.debug) {
        console.warn('[ACTR] Failed to get active profile:', error);
      }
      return null;
    }
  }

  /**
   * Check context rules
   */
  private checkContextRules(rules: string[] | undefined, context: ActionContext): void {
    if (!rules || rules.length === 0) return;
    for (const rule of rules) {
      if (!this.evaluateRule(rule, context)) {
        throw new Error(`Context rule failed: ${rule}`);
      }
    }
  }

  /**
   * Evaluate a context rule
   */
  private evaluateRule(rule: string, context: ActionContext): boolean {
    // Built-in rules
    switch (rule) {
      case 'hasActiveNode':
        return !!context.data?.activeNode;
      case 'isEditable':
        return context.permissions.includes('edit');
      case 'isAdmin':
        return context.permissions.includes('admin');
      default:
        console.warn(`[ACTR] Unknown context rule: ${rule}`);
        return true;
    }
  }

  /**
   * Check permissions using Profylr profile + Permittr
   * 
   * PROFYLR INTEGRATION:
   * 1. Get active profile from Profylr
   * 2. Check profile.access permissions for the action
   * 3. Fall back to Permittr if profile doesn't define permissions
   * 4. Fall back to context permissions if neither available
   */
  private async checkPermissions(
    required: string[] | undefined,
    context: ActionContext,
    action: string | null = null,
    resource: string | null = null,
    resourceId: string | null = null
  ): Promise<void> {
    if (!required || required.length === 0) return;
    
    // 1. Try Profylr profile permissions first
    if (this.profylr) {
      try {
        const profile = await this.getActiveProfile();
        if (profile?.access) {
          const userId = context.user?.id || context.user?.email || 'anonymous';
          
          // Check if action is defined in profile permissions
          if (action && profile.access.permissions && profile.access.permissions[action]) {
            const allowedRoles = profile.access.permissions[action];
            const userRoles = context.user?.roles || [];
            
            const hasPermission = allowedRoles.some((role: string) => 
              userRoles.includes(role) || role === 'all'
            );
            
            if (!hasPermission) {
              this.log('warn', `[ACTR] Profile permission denied: ${action}`, {
                userId,
                requiredRoles: allowedRoles,
                userRoles
              });
              throw new Error(`Permission denied by profile: ${action}`);
            }
            
            if (this.options.debug) {
              console.log(`[ACTR] Profile permission granted: ${action}`);
            }
            return; // Permission granted by profile
          }
        }
      } catch (error) {
        if (this.options.debug) {
          console.warn('[ACTR] Profile permission check failed, falling back:', error);
        }
      }
    }
    
    // 2. Fall back to Permittr if available
    if (this.options.permittr) {
      const userId = context.user?.id;
      
      if (!userId) {
        throw new Error('User ID required for permission check');
      }
      
      // Check each required permission through Permittr
      for (const permission of required) {
        // Use Permittr's check method
        const allowed = this.options.permittr.check(
          userId,
          action || permission,
          resource || 'system',
          resourceId || '*',
          context
        );
        
        if (!allowed) {
          this.log('warn', `[ACTR] Permission denied: ${permission}`, {
            userId,
            action,
            resource,
            resourceId
          });
          throw new Error(`Permission denied: ${permission}`);
        }
      }
      
      return; // All permissions granted
    }
    
    // 3. Fallback to simple permission array check (if neither Profylr nor Permittr available)
    if (this.options.debug) {
      console.warn('[ACTR] Neither Profylr nor Permittr configured, using fallback permission check');
    }
    
    for (const permission of required) {
      if (!context.permissions.includes(permission)) {
        throw new Error(`Missing permission: ${permission}`);
      }
    }
  }

  /**
   * Log message
   */
  private log(level: string, message: string, data: any = {}): void {
    if (this.options.logger) {
      this.options.logger.log(level, message, data);
    } else if (this.options.debug) {
      console.log(`[${level.toUpperCase()}]`, message, data);
    }
  }

  /**
   * Get all registered actions
   */
  getActions(): Array<{name: string; description?: string; permissions?: string[]; contextRules?: string[]; metadata?: any}> {
    return Array.from(this.actions.entries()).map(([name, config]) => ({
      name,
      description: config.description,
      permissions: config.permissions,
      contextRules: config.contextRules,
      metadata: config.metadata
    }));
  }

  /**
   * Clear context cache
   */
  clearContextCache(): void {
    this.contextCache = null;
    this.contextCacheTime = 0;
  }

  // ========================================
  // CRUD OPERATIONS (Data Layer)
  // ========================================

  /**
   * Create a new entity
   * Delegates to Aggregatr for data operations
   */
  async create(entityType: string, data: EntityData): Promise<EntityData> {
    const startTime = Date.now();

    try {
      // Get context
      const context = await this.getContext();

      // Check permissions (using Permittr if available)
      this.checkPermissions(['create'], context, 'create', entityType, data.id || 'new');

      // Run before middleware
      let processedData = { ...data };
      for (const middleware of this.middleware.before) {
        processedData = await middleware(`create:${entityType}`, processedData, context) || processedData;
      }

      // Log operation
      this.log('info', `[ACTR] Creating ${entityType}`, { data: processedData });

      // Delegate to Aggregatr
      const entity = await this.aggregatr.create(entityType, processedData);

      // Run after middleware
      for (const middleware of this.middleware.after) {
        await middleware(`create:${entityType}`, entity, context);
      }

      // Log completion
      const duration = Date.now() - startTime;
      this.log('info', `[ACTR] Created ${entityType}:${entity.id} (${duration}ms)`);

      return entity;

    } catch (error) {
      // Run error middleware
      const context = await this.getContext();
      const err = error instanceof Error ? error : new Error(String(error));
      
      for (const middleware of this.middleware.error) {
        await middleware(`create:${entityType}`, err, context);
      }

      // Log error
      const duration = Date.now() - startTime;
      this.log('error', `[ACTR] Create failed for ${entityType} (${duration}ms)`, {
        error: err.message,
        data
      });

      throw err;
    }
  }

  /**
   * Read an entity
   * Delegates to Aggregatr for data operations
   */
  async read(entityType: string, id: string): Promise<EntityData | null> {
    try {
      this.log('debug', `[ACTR] Reading ${entityType}:${id}`);
      
      // Delegate to Aggregatr
      const entity = await this.aggregatr.read(entityType, id);
      
      if (!entity) {
        this.log('debug', `[ACTR] ${entityType}:${id} not found`);
      }
      
      return entity;

    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.log('error', `[ACTR] Read failed for ${entityType}:${id}`, {
        error: err.message
      });
      throw err;
    }
  }

  /**
   * Update an entity
   * Delegates to Aggregatr for data operations
   */
  async update(entityType: string, id: string, updates: Partial<EntityData>): Promise<EntityData> {
    const startTime = Date.now();

    try {
      // Get existing entity for before state
      const existing = await this.aggregatr.read(entityType, id);
      if (!existing) {
        throw new Error(`${entityType}:${id} not found`);
      }

      // Get context and store before state for undo
      const context = await this.getContext();
      context._beforeState = { ...existing }; // Store for Backtrackr middleware

      // Check permissions (using Permittr if available)
      this.checkPermissions(['update'], context, 'update', entityType, id);

      // Run before middleware
      let processedUpdates = updates;
      for (const middleware of this.middleware.before) {
        processedUpdates = await middleware(`update:${entityType}`, processedUpdates, context) || processedUpdates;
      }

      // Log operation
      this.log('info', `[ACTR] Updating ${entityType}:${id}`, { updates: processedUpdates });

      // Delegate to Aggregatr
      const entity = await this.aggregatr.update(entityType, id, processedUpdates);

      // Run after middleware
      for (const middleware of this.middleware.after) {
        await middleware(`update:${entityType}`, entity, context);
      }

      // Log completion
      const duration = Date.now() - startTime;
      this.log('info', `[ACTR] Updated ${entityType}:${id} (${duration}ms)`);

      return entity;

    } catch (error) {
      // Run error middleware
      const context = await this.getContext();
      const err = error instanceof Error ? error : new Error(String(error));
      
      for (const middleware of this.middleware.error) {
        await middleware(`update:${entityType}`, err, context);
      }

      // Log error
      const duration = Date.now() - startTime;
      this.log('error', `[ACTR] Update failed for ${entityType}:${id} (${duration}ms)`, {
        error: err.message,
        updates
      });

      throw err;
    }
  }

  /**
   * Delete an entity
   * Delegates to Aggregatr for data operations
   */
  async delete(entityType: string, id: string): Promise<boolean> {
    const startTime = Date.now();

    try {
      // Get entity before deletion (for undo)
      const entity = await this.aggregatr.read(entityType, id);
      
      // Get context
      const context = await this.getContext();

      // Check permissions (using Permittr if available)
      this.checkPermissions(['delete'], context, 'delete', entityType, id);

      // Run before middleware
      for (const middleware of this.middleware.before) {
        await middleware(`delete:${entityType}`, { id }, context);
      }

      // Log operation
      this.log('info', `[ACTR] Deleting ${entityType}:${id}`);

      // Delegate to Aggregatr (handles entity + relationships)
      const deleted = await this.aggregatr.delete(entityType, id);

      if (deleted) {
        // Run after middleware with deleted entity for undo
        for (const middleware of this.middleware.after) {
          await middleware(`delete:${entityType}`, { id, deleted: true, _deletedEntity: entity }, context);
        }

        // Log completion
        const duration = Date.now() - startTime;
        this.log('info', `[ACTR] Deleted ${entityType}:${id} (${duration}ms)`);
      } else {
        this.log('warn', `[ACTR] ${entityType}:${id} not found for deletion`);
      }

      return deleted;

    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      // Run error middleware
      const context = await this.getContext();
      
      for (const middleware of this.middleware.error) {
        await middleware(`delete:${entityType}`, err, context);
      }

      // Log error
      const duration = Date.now() - startTime;
      this.log('error', `[ACTR] Delete failed for ${entityType}:${id} (${duration}ms)`, {
        error: err.message
      });

      throw err;
    }
  }

  /**
   * Query entities
   * Delegates to Aggregatr for data operations
   */
  async query(entityType: string, filters?: QueryFilters): Promise<EntityData[]> {
    try {
      this.log('debug', `[ACTR] Querying ${entityType}`, { filters });
      
      // Delegate to Aggregatr
      const entities = await this.aggregatr.query(entityType, filters);
      
      this.log('debug', `[ACTR] Query returned ${entities.length} ${entityType}(s)`);
      return entities;

    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.log('error', `[ACTR] Query failed for ${entityType}`, {
        error: err.message
      });
      throw err;
    }
  }

  // ========================================
  // RELATIONSHIP OPERATIONS
  // ========================================

  /**
   * Create a relationship between entities
   * Delegates to Aggregatr for data operations
   */
  async relate(entityType: string, id: string, relationType: string, targetId: string): Promise<void> {
    try {
      this.log('debug', `[ACTR] Relating ${entityType}:${id} -> ${relationType} -> ${targetId}`);
      
      // Delegate to Aggregatr
      await this.aggregatr.relate(entityType, id, relationType, targetId);

    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.log('error', `[ACTR] Relate failed`, {
        error: err.message,
        entityType,
        id,
        relationType,
        targetId
      });
      throw err;
    }
  }

  /**
   * Remove a relationship between entities
   * Delegates to Aggregatr for data operations
   */
  async unrelate(entityType: string, id: string, relationType: string, targetId: string): Promise<void> {
    try {
      this.log('debug', `[ACTR] Unrelating ${entityType}:${id} -> ${relationType} -> ${targetId}`);
      
      // Delegate to Aggregatr
      await this.aggregatr.unrelate(entityType, id, relationType, targetId);

    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.log('error', `[ACTR] Unrelate failed`, {
        error: err.message,
        entityType,
        id,
        relationType,
        targetId
      });
      throw err;
    }
  }

  /**
   * Get related entities
   * Delegates to Aggregatr for data operations
   */
  async getRelated(entityType: string, id: string, relationType: string): Promise<EntityData[]> {
    try {
      this.log('debug', `[ACTR] Getting related ${entityType}:${id} -> ${relationType}`);
      
      // Delegate to Aggregatr
      const related = await this.aggregatr.getRelated(entityType, id, relationType);
      
      return related;

    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.log('error', `[ACTR] GetRelated failed`, {
        error: err.message,
        entityType,
        id,
        relationType
      });
      throw err;
    }
  }
}

export default Actr;
